<script lang="ts">
	import PresenceGrid from './Items/PresenceGrid.svelte';
	import Furnitures from './Furnitures.svelte';
	import ZoneDrawPanel from './sidebar/ZoneDrawPanel.svelte';
	import RoomDrawPanel from './sidebar/RoomDrawPanel.svelte';
	import RoomSettingsPanel from './sidebar/RoomSettingsPanel.svelte';
	import ZonesBoundariesList from './sidebar/ZonesBoundariesList.svelte';
	import SensorConfigTab from './tabs/SensorConfigTab.svelte';
	import {
		addAreaOptimized,
		subtractAreaOptimized,
		intersectZoneRects,
		normalizeZoneRect,
		zoneAreaToCellKeys,
		ZONE_COLOR_PALETTE,
		ZONE_LIMIT,
		zoneEnabledFromDevice,
	} from '../helpers/radar-helper';
	import {
		addZoneRequest,
		deleteZoneRequest,
		setBlindZonesAndRoomBoundaries,
		updateZoneRequest,
		updateZoneConfigRequest,
	} from '../helpers/requests';
	import type { Device } from 'src/types/Device';
	import { Col, Row, toast, Tabs, Tab } from '@Shelly/shelly-design-system/index';
	import type { Zone } from '../Types';
	import { format } from 'svelte-i18n';

	type ActiveTab = 'zones' | 'furnitures' | 'config';
	import { PresenceActiveTabStore } from 'src/components/Radar/stores/presence';
	import { selectFurniture } from 'src/stores/features/radar/furniture';

	// ── Props ────────────────────────────────────────────────────────────────
	export let zones: Zone[] = [];
	export let radarPoints: any[] = [];
	export let simulatedPoints: any[] = [];
	export let device: Device;
	export let editable = false;
	export let showModal = false;
	export let showSettings = false;
	export let config: any = {};
	export let room: [number, number, number, number][] = [];
	export let blind: [number, number, number, number][] = [];
	export let settings: any = [];

	// ── Local state ──────────────────────────────────────────────────────────
	let localZones: Zone[] = zones ? [...zones] : [];
	/** Zone IDs hidden from the grid — purely local, no API call. */
	let hiddenZoneIds = new Set<number>();

	function toggleZoneVisibility(e: CustomEvent<Zone>) {
		const zone = e.detail;
		if (!zone) return;
		hiddenZoneIds = new Set(
			hiddenZoneIds.has(zone.id)
				? [...hiddenZoneIds].filter((id) => id !== zone.id)
				: [...hiddenZoneIds, zone.id],
		);
	}
	let roomBlindSpots = blind || [];
	let roomBoundaries = room || [];
	let editingZone: Zone | null = null;
	let editingRoom = false;
	/** Guards the $: if (!editingRoom) reactive block from resetting roomBlindSpots
	 *  when editingRoom briefly goes false (e.g. during resetEditingState inside editRoom).
	 *  Set true before entering room edit, cleared when explicitly leaving. */
	let roomEditing = false;
	let editingMode: 'draw' | 'erase' = 'draw';
	let initialZoneArea: [number, number, number, number][] | null = null;
	let initialZoneName: string = '';
	let initialZoneColor: number[] = [];
	let initialRoomBlindSpots: [number, number, number, number][] | null = null;
	let initialRoomBoundaries: [number, number, number, number][] | null = null;
	let showCoordinates = false;
	let activeTab: ActiveTab = 'zones';
	let activeTabIndex = 0;
	let activeRoomEditType: 'blind_spots' | 'room_boundaries' = 'blind_spots';
	/** True while the user is actively drawing/erasing a zone's area on the grid. */
	let drawingMode: boolean = false;
	let drawModeName: string = '';
	let drawModeColor: number[] = [0, 120, 255];
	let gridRef: any;

	// Optimistic state: prevents the reactive reset from overwriting just-saved data
	// before the device confirms and the parent re-renders with fresh props.
	let pendingOptimisticZones: Zone[] | null = null;
	let pendingOptimisticRoom: {
		blind: [number, number, number, number][];
		boundaries: [number, number, number, number][];
	} | null = null;

	let renderedGridSize = 300;
	let showRoomSettings = false;
	/** Clears dirty state on {@link RoomSettingsPanel} after a successful room save. */
	let roomSettingsPanelRef: { acknowledgeSaved?: () => void } | undefined;
	let zoneSaving = false;
	let roomSaving = false;
	let roomSettingsSaving = false;

	$: tabItems = [
		{
			text: $format('presence_tab_zones_and_boundaries', { default: 'Zones and boundaries' }),
			key: 'zones',
		},
		{ text: $format('presence_tab_furniture', { default: 'Furniture' }), key: 'furnitures' },
		// {
		// 	text: $format('presence_tab_sensor_config', { default: 'Sensor configuration' }),
		// 	key: 'config',
		// },
	];

	// ── Reactive ─────────────────────────────────────────────────────────────
	$: mainZoneId = config?.main_zone ? parseInt(config.main_zone.split(':')[1]) : null;
	$: mainZoneColor =
		mainZoneId != null ? (localZones.find((z) => z.id === mainZoneId)?.color ?? null) : null;
	$: roomTintRgbForGrid =
		mainZoneColor != null && mainZoneColor.length >= 3
			? ([mainZoneColor[0], mainZoneColor[1], mainZoneColor[2]] as [number, number, number])
			: null;
	$: zoneHasChanges =
		editingZone != null &&
		(JSON.stringify(editingZone.area) !== JSON.stringify(initialZoneArea) ||
			editingZone.name !== initialZoneName ||
			JSON.stringify(editingZone.color) !== JSON.stringify(initialZoneColor));
	$: roomHasChanges =
		editingRoom &&
		((initialRoomBlindSpots !== null &&
			JSON.stringify(roomBlindSpots) !== JSON.stringify(initialRoomBlindSpots)) ||
			(initialRoomBoundaries !== null &&
				JSON.stringify(roomBoundaries) !== JSON.stringify(initialRoomBoundaries)));

	/** Stable fingerprint for comparing pre/post merge (avoids reactive churn / feedback loops). */
	function zoneEditableMergeSignature(z: Zone): string {
		return JSON.stringify({
			n: z.name,
			c: z.color,
			a: z.area,
			e: z.enabled,
			zn: z.zmin,
			zx: z.zmax,
			f: z.furnitures,
			m: (z as any).meta,
		});
	}

	/** Merge device/parent zone into the row being edited; keep dirty name/color/area/furniture. */
	function mergeEditingZoneFromServer(
		current: Zone,
		fresh: Zone,
		initArea: [number, number, number, number][] | null,
		initName: string,
		initColor: number[],
	): Zone {
		const nameDirty = current.name !== initName;
		const colorDirty = JSON.stringify(current.color) !== JSON.stringify(initColor);
		const areaDirty = initArea !== null && JSON.stringify(current.area) !== JSON.stringify(initArea);
		const geoDirty = nameDirty || colorDirty || areaDirty;

		const serverEnabled = zoneEnabledFromDevice(fresh);
		const enableDirty = current.enabled !== serverEnabled;

		const merged: Zone = {
			...fresh,
			enabled: enableDirty ? current.enabled : serverEnabled,
			...(current.isNew ? { isNew: true } : {}),
		};

		if (nameDirty) merged.name = current.name;
		if (colorDirty) merged.color = [...current.color];
		if (areaDirty && initArea !== null) merged.area = structuredClone(current.area);
		if (geoDirty) merged.furnitures = current.furnitures ?? merged.furnitures;

		return merged;
	}

	/** Full row from props for zones the user is not currently drawing. */
	function mergeNonEditingZoneFromServer(fresh: Zone, prevRow: Zone | undefined): Zone {
		if (prevRow?.isNew) return prevRow;
		return { ...fresh, enabled: zoneEnabledFromDevice(fresh) };
	}

	$: if (!editingZone) {
		if (pendingOptimisticZones) {
			localZones = pendingOptimisticZones;
			pendingOptimisticZones = null;
		} else {
			localZones = zones ? [...zones] : [];
		}
	} else if (zones && editingZone) {
		// Websocket / parent can refresh `zones` while the modal is open. Merge server
		// truth into local state without clobbering in-progress name/color/area edits.
		const prevById = new Map(localZones.map((z) => [z.id, z]));
		const ez = editingZone;
		const freshForEdit = zones.find((z) => z.id === ez.id);
		let nextEditing = ez;
		const beforeSig = zoneEditableMergeSignature(ez);

		if (freshForEdit && !ez.isNew) {
			nextEditing = mergeEditingZoneFromServer(
				ez,
				freshForEdit,
				initialZoneArea,
				initialZoneName,
				initialZoneColor,
			);

			const nameDirty = ez.name !== initialZoneName;
			const colorDirty = JSON.stringify(ez.color) !== JSON.stringify(initialZoneColor);
			const areaDirty =
				initialZoneArea !== null && JSON.stringify(ez.area) !== JSON.stringify(initialZoneArea);

			if (zoneEditableMergeSignature(nextEditing) !== beforeSig) {
				if (!nameDirty) {
					initialZoneName = nextEditing.name;
					drawModeName = nextEditing.name;
				}
				if (!colorDirty) {
					initialZoneColor = [...(nextEditing.color || [])];
					drawModeColor = [...(nextEditing.color || [])];
				}
				if (!areaDirty && initialZoneArea !== null) {
					initialZoneArea = structuredClone(nextEditing.area);
				}
			}
		}

		if (zoneEditableMergeSignature(nextEditing) !== beforeSig) {
			editingZone = nextEditing;
		}
		const activeId = nextEditing.id;
		const serverIds = new Set(zones.map((z) => z.id));

		localZones = [
			...zones.map((freshZ) =>
				freshZ.id === activeId
					? { ...nextEditing }
					: mergeNonEditingZoneFromServer(freshZ, prevById.get(freshZ.id)),
			),
			...localZones.filter((lz) => lz.isNew && !serverIds.has(lz.id)),
		];
	}

	$: if (!editingRoom && !roomEditing) {
		if (pendingOptimisticRoom) {
			roomBlindSpots = [...pendingOptimisticRoom.blind];
			roomBoundaries = [...pendingOptimisticRoom.boundaries];
			pendingOptimisticRoom = null;
		} else {
			roomBlindSpots = blind || [];
			roomBoundaries = room || [];
		}
	}

	$: if (editingRoom && roomEditing) {
		const blindDirty =
			initialRoomBlindSpots !== null &&
			JSON.stringify(roomBlindSpots) !== JSON.stringify(initialRoomBlindSpots);
		const boundariesDirty =
			initialRoomBoundaries !== null &&
			JSON.stringify(roomBoundaries) !== JSON.stringify(initialRoomBoundaries);

		if (!blindDirty) {
			const nextBlind = blind || [];
			if (JSON.stringify(roomBlindSpots) !== JSON.stringify(nextBlind)) {
				roomBlindSpots = [...nextBlind];
				initialRoomBlindSpots = structuredClone(nextBlind);
			}
		}

		if (!boundariesDirty) {
			const nextRoom = room || [];
			if (JSON.stringify(roomBoundaries) !== JSON.stringify(nextRoom)) {
				roomBoundaries = [...nextRoom];
				initialRoomBoundaries = structuredClone(nextRoom);
			}
		}
	}

	$: if (showSettings && !showModal) {
		showModal = true;
	}

	// ── Utilities ─────────────────────────────────────────────────────────────
	function confirmDiscardChanges(): boolean {
		if (!zoneHasChanges && !roomHasChanges) return true;
		return confirm(
			$format('presence_toast_unsaved_confirm', {
				default: 'You have unsaved changes that will be removed. Continue?',
			}),
		);
	}

	function resetEditingState() {
		editingZone = null;
		editingRoom = false;
		drawingMode = false;
		initialZoneArea = null;
		initialZoneName = '';
		initialZoneColor = [];
		initialRoomBlindSpots = null;
		initialRoomBoundaries = null;
	}

	/** Exit editing without changes — no toast, just clean up. */
	function returnEditing() {
		if (editingZone && initialZoneArea !== null) {
			editingZone.area = structuredClone(initialZoneArea);
			editingZone = { ...editingZone, name: initialZoneName, color: initialZoneColor };
			updateLocalZone();
		}
		if (editingRoom) {
			if (initialRoomBlindSpots) roomBlindSpots = structuredClone(initialRoomBlindSpots);
			if (initialRoomBoundaries) roomBoundaries = structuredClone(initialRoomBoundaries);
		}
		resetEditingState();
		roomEditing = false;
	}

	function updateLocalZone() {
		if (!editingZone) return;
		localZones = localZones.map((z) => (z.id === editingZone.id ? { ...editingZone } : z));
	}

	// ── Zone management ───────────────────────────────────────────────────────
	function createNewZone() {
		if (!confirmDiscardChanges()) return;
		const newZone: Zone = {
			id: Date.now(),
			name: 'New Zone',
			enabled: true,
			zmin: 0,
			zmax: 3,
			color: [0, 0, 255],
			area: [],
			visible: true,
			isNew: true,
		};
		localZones = [...localZones, newZone];
		editingZone = newZone;
		drawModeName = newZone.name;
		drawModeColor = [...newZone.color];
		initialZoneArea = [];
		initialZoneName = newZone.name;
		initialZoneColor = [...newZone.color];
		editingMode = 'draw';
		drawingMode = true;
	}

	function cancelEditing() {
		let changesCanceled = false;

		if (editingZone && initialZoneArea) {
			editingZone.area = structuredClone(initialZoneArea);
			updateLocalZone();
			changesCanceled = true;
		}
		if (editingRoom) {
			if (initialRoomBlindSpots) roomBlindSpots = structuredClone(initialRoomBlindSpots);
			if (initialRoomBoundaries) roomBoundaries = structuredClone(initialRoomBoundaries);
			changesCanceled = true;
		}

		if (changesCanceled) {
			toast.open({
				type: 'info',
				title: $format('presence_toast_changes_discarded', {
					default:
						'Possible changes discarded. Settings have been restored to their previous state.',
				}),
			});
		}

		resetEditingState();
		roomEditing = false;
	}

	async function saveZone() {
		if (!editingZone) return;
		zoneSaving = true;

		const isNew = Boolean(editingZone.isNew);
		const savedZone = { ...editingZone };
		const tempId = savedZone.id;

		try {
			let result;
			if (isNew) {
				result = await addZoneRequest(
					device,
					savedZone.name,
					savedZone.enabled,
					Number(savedZone.zmin),
					Number(savedZone.zmax),
					savedZone.color,
					savedZone.area,
				);
				if (result?.id !== undefined) {
					localZones = localZones.map((z) =>
						z.id === tempId ? { ...z, id: result.id, isNew: false } : z,
					);
				}
			} else {
				result = await updateZoneRequest(
					Number(savedZone.id),
					device,
					savedZone.name,
					savedZone.enabled,
					Number(savedZone.zmin),
					Number(savedZone.zmax),
					savedZone.color,
					savedZone.area,
				);
			}

			if (result?.isok || result?.id !== undefined) {
				const newId = isNew && result?.id !== undefined ? result.id : savedZone.id;
				if (isNew && result?.id !== undefined) {
					localZones = localZones.map((z) =>
						z.id === tempId ? { ...savedZone, id: result.id, isNew: false } : z,
					);
				} else {
					localZones = localZones.map((z) => (z.id === savedZone.id ? { ...savedZone } : z));
				}
				const ez = localZones.find((z) => z.id === newId);
				if (!ez) throw new Error('Save failed');
				editingZone = { ...ez };
				initialZoneArea = structuredClone(editingZone.area);
				initialZoneName = editingZone.name;
				initialZoneColor = [...(editingZone.color || [0, 120, 255])];
				drawModeName = editingZone.name;
				drawModeColor = [...(editingZone.color || [0, 120, 255])];
				pendingOptimisticZones = localZones.map((z) =>
					z.id === newId ? { ...editingZone! } : z,
				);
				toast.open({
					type: 'success',
					title: isNew
						? $format('presence_toast_zone_created', {
								default: 'Zone created successfully.',
							})
						: $format('presence_toast_zone_updated', {
								default: 'Zone updated successfully.',
							}),
				});
			} else {
				throw new Error('Save failed');
			}
		} catch (error) {
			console.error('Error saving zone:', error);
			localZones = [...zones];
			toast.open({
				type: 'danger',
				title: isNew
					? $format('presence_toast_zone_add_error', { default: 'Error adding zone.' })
					: $format('presence_toast_zone_update_error', { default: 'Error updating zone.' }),
			});
		} finally {
			zoneSaving = false;
		}
	}

	async function deleteZone(e: CustomEvent<Zone>) {
		const zoneToDelete = e.detail;
		if (!zoneToDelete) return;

		try {
			const result = await deleteZoneRequest(zoneToDelete.id, device);
			if (result.isok) {
				localZones = localZones.filter((z) => z.id !== zoneToDelete.id);
				if (editingZone?.id === zoneToDelete.id) resetEditingState();
				toast.open({
					type: 'success',
					title: $format('presence_toast_zone_deleted', {
						default: 'Zone deleted successfully.',
					}),
				});
			} else {
				throw new Error('Delete failed');
			}
		} catch (error) {
			console.error('Error deleting zone:', error);
			toast.open({
				type: 'danger',
				title: $format('presence_toast_zone_delete_error', { default: 'Error deleting zone.' }),
			});
		}
	}

	// ── Zone enable / disable (immediate — does not wait for full Save) ──────
	async function handleZoneEnabledChange(e: CustomEvent<boolean>) {
		if (!editingZone) return;
		const newEnabled = e.detail;
		const zoneId = editingZone.id;
		const zoneMeta = (editingZone as any).meta || {};

		// Update local state immediately (optimistic)
		editingZone = { ...editingZone, enabled: newEnabled };
		localZones = localZones.map((z) => (z.id === zoneId ? { ...z, enabled: newEnabled } : z));

		const revert = () => {
			editingZone = { ...editingZone!, enabled: !newEnabled };
			localZones = localZones.map((z) => (z.id === zoneId ? { ...z, enabled: !newEnabled } : z));
		};

		try {
			const result = await updateZoneConfigRequest(device, Number(zoneId), {
				enable: newEnabled,
				meta: zoneMeta,
			});
			if (!result?.isok) {
				revert();
				toast.open({
					type: 'danger',
					title: $format('presence_toast_zone_update_error', {
						default: 'Error updating zone.',
					}),
				});
				return;
			}
			toast.open({
				type: 'success',
				title: $format('presence_toast_zone_updated', {
					default: 'Zone updated successfully.',
				}),
			});
		} catch (error) {
			revert();
			console.error('Error toggling zone enabled:', error);
			toast.open({
				type: 'danger',
				title: $format('presence_toast_zone_update_error', { default: 'Error updating zone.' }),
			});
		}
	}

	// ── Room management ───────────────────────────────────────────────────────
	function editRoom() {
		if (!confirmDiscardChanges()) return;
		roomEditing = true;
		resetEditingState();
		editingRoom = true;
		roomBlindSpots = blind || [];
		roomBoundaries = room || [];
		initialRoomBlindSpots = structuredClone(roomBlindSpots);
		initialRoomBoundaries = structuredClone(roomBoundaries);
	}

	async function saveRoom() {
		if (!editingRoom) return;
		roomSaving = true;

		try {
			const mainZoneKey = config?.main_zone;
			const mzId = mainZoneKey ? parseInt(mainZoneKey.split(':')[1]) : undefined;
			await setBlindZonesAndRoomBoundaries(device, roomBlindSpots, roomBoundaries, mzId);
			pendingOptimisticRoom = {
				blind: structuredClone(roomBlindSpots) as [number, number, number, number][],
				boundaries: structuredClone(roomBoundaries) as [number, number, number, number][],
			};
			initialRoomBlindSpots = structuredClone(roomBlindSpots);
			initialRoomBoundaries = structuredClone(roomBoundaries);
			toast.open({
				type: 'success',
				title: $format('presence_toast_room_saved', {
					default: 'Room settings saved successfully.',
				}),
			});
		} catch (error) {
			console.error('Error saving room configuration:', error);
			toast.open({
				type: 'danger',
				title: $format('presence_toast_room_save_error', {
					default: 'Error saving room configuration.',
				}),
			});
		} finally {
			roomSaving = false;
		}
	}

	// ── Tab management ────────────────────────────────────────────────────────
	function switchTab(newTab: ActiveTab) {
		if (!confirmDiscardChanges()) return;

		activeTab = newTab;
		activeTabIndex = tabItems.findIndex((item) => item.key === newTab);
		roomEditing = false;
		resetEditingState();

		if (newTab !== 'furnitures') {
			selectFurniture(null);
		}
		PresenceActiveTabStore.set(activeTab);
	}

	// ── Draw handlers ─────────────────────────────────────────────────────────
	function handleDraw(event: CustomEvent) {
		const { zoneRect, mode } = event.detail;
		if (!editingZone) return;

		const normalizedStroke = normalizeZoneRect(zoneRect);
		let rectsToApply: [number, number, number, number][] = [normalizedStroke];
		if (mode === 'draw' && roomBoundaries?.length > 0) {
			rectsToApply = roomBoundaries.flatMap((b) => {
				const inter = intersectZoneRects(normalizedStroke, b);
				return inter ? [inter] : [];
			});
		}

		const afterRoomClip = rectsToApply;

		// Zones cannot overlap blind spots
		if (mode === 'draw' && roomBlindSpots?.length > 0) {
			for (const blindRect of roomBlindSpots) {
				rectsToApply = subtractAreaOptimized(rectsToApply, blindRect);
			}
		}

		if (mode === 'draw' && rectsToApply.length === 0) {
			if (roomBoundaries?.length > 0 && afterRoomClip.length === 0) {
				toast.open({
					type: 'info',
					title: $format('presence_toast_draw_clip_outside_room', {
						default:
							'Nothing was added. Draw inside the room boundary (the highlighted area on the grid).',
					}),
				});
			} else {
				toast.open({
					type: 'info',
					title: $format('presence_toast_draw_clip_blind_only', {
						default: 'Nothing was added. Zones cannot cover blind spots.',
					}),
				});
			}
			return;
		}

		for (const rect of rectsToApply) {
			editingZone.area =
				mode === 'draw'
					? addAreaOptimized(editingZone.area, rect)
					: subtractAreaOptimized(editingZone.area, rect);
		}
		updateLocalZone();
	}

	function handleRoomDraw(event: CustomEvent) {
		if (!editingRoom) return;
		const { zoneRect, mode, type } = event.detail;
		const currentArea = type === 'blind_spots' ? roomBlindSpots : roomBoundaries;

		const normalizedStroke = normalizeZoneRect(zoneRect);
		let rectsToApply: [number, number, number, number][] = [normalizedStroke];
		if (mode === 'draw' && type === 'blind_spots' && roomBoundaries?.length > 0) {
			rectsToApply = roomBoundaries.flatMap((b) => {
				const inter = intersectZoneRects(normalizedStroke, b);
				return inter ? [inter] : [];
			});
			if (rectsToApply.length === 0) {
				toast.open({
					type: 'info',
					title: $format('presence_toast_draw_clip_outside_room', {
						default:
							'Nothing was added. Draw inside the room boundary (the highlighted area on the grid).',
					}),
				});
				return;
			}
		}

		// Room boundaries cannot be drawn over secondary zones or blind spots.
		// The main / room zone is excluded: it usually covers the same area as the boundary
		// interior, so subtracting it would block extending the room boundary entirely.
		if (mode === 'draw' && type === 'room_boundaries') {
			const strokeCellKeys = zoneAreaToCellKeys([normalizedStroke]);
			for (const zone of localZones) {
				if (mainZoneId != null && zone.id === mainZoneId) continue;
				for (const zoneAreaRect of zone.area ?? []) {
					rectsToApply = subtractAreaOptimized(
						rectsToApply,
						zoneAreaRect as [number, number, number, number],
					);
				}
			}
			for (const blindRect of roomBlindSpots) {
				rectsToApply = subtractAreaOptimized(rectsToApply, blindRect);
			}
			if (rectsToApply.length === 0) {
				toast.open({
					type: 'info',
					title: $format('presence_toast_room_boundary_clip_full', {
						default:
							'Nothing was added. Room boundary cannot overlap existing zones or blind spots.',
					}),
				});
				return;
			}
			const clippedCellKeys = zoneAreaToCellKeys(rectsToApply);
			if (clippedCellKeys.size < strokeCellKeys.size) {
				toast.open({
					type: 'info',
					title: $format('presence_toast_room_boundary_clip_partial', {
						default:
							'Part of the room boundary was not added because it overlaps a zone or blind spot.',
					}),
				});
			}
		}

		// Room boundary draw: nothing new if the stroke is already entirely inside the current boundary
		if (mode === 'draw' && type === 'room_boundaries') {
			const cellsToApply = zoneAreaToCellKeys(rectsToApply);
			const boundaryKeys = zoneAreaToCellKeys(roomBoundaries);
			let addsNewCells = false;
			for (const k of cellsToApply) {
				if (!boundaryKeys.has(k)) {
					addsNewCells = true;
					break;
				}
			}
			if (!addsNewCells) {
				toast.open({
					type: 'info',
					title: $format('presence_toast_room_boundary_draw_redundant', {
						default: 'This area is already part of the room boundary.',
					}),
				});
				return;
			}
		}

		// Room boundary erase: nothing to remove if the stroke does not overlap the current boundary
		if (mode === 'erase' && type === 'room_boundaries') {
			const eraseKeys = zoneAreaToCellKeys(rectsToApply);
			const boundaryKeys = zoneAreaToCellKeys(roomBoundaries);
			let overlapsBoundary = false;
			for (const k of eraseKeys) {
				if (boundaryKeys.has(k)) {
					overlapsBoundary = true;
					break;
				}
			}
			if (!overlapsBoundary) {
				toast.open({
					type: 'info',
					title: $format('presence_toast_room_boundary_erase_redundant', {
						default: 'Nothing to erase here. Drag over the existing room boundary.',
					}),
				});
				return;
			}
		}

		let newArea = currentArea;
		for (const rect of rectsToApply) {
			newArea =
				mode === 'draw' ? addAreaOptimized(newArea, rect) : subtractAreaOptimized(newArea, rect);
		}

		if (type === 'blind_spots') {
			roomBlindSpots = newArea;
		} else {
			roomBoundaries = newArea;
		}
	}

	// ── Zone edit event handlers ───────────────────────────────────────────────
	function handleZoneEdit(event: CustomEvent) {
		const zone = event.detail as Zone;
		if ((drawingMode || editingZone || editingRoom) && !confirmDiscardChanges()) return;
		resetEditingState();
		editingZone = { ...zone, enabled: zoneEnabledFromDevice(zone) };
		drawModeName = zone.name;
		drawModeColor = [...(zone.color || [0, 120, 255])];
		initialZoneArea = structuredClone(zone.area);
		initialZoneName = zone.name;
		initialZoneColor = [...(zone.color || [0, 120, 255])];
		editingMode = 'draw';
		drawingMode = true;
	}

	function handleBlindSpotsEdit() {
		if ((drawingMode || editingZone || editingRoom) && !confirmDiscardChanges()) return;
		resetEditingState();
		editingRoom = true;
		roomEditing = true;
		activeRoomEditType = 'blind_spots';
		editingMode = 'draw';
		initialRoomBlindSpots = structuredClone(roomBlindSpots);
		initialRoomBoundaries = structuredClone(roomBoundaries);
	}

	function handleRoomBoundaryEdit() {
		if ((drawingMode || editingZone || editingRoom) && !confirmDiscardChanges()) return;
		resetEditingState();
		editingRoom = true;
		roomEditing = true;
		activeRoomEditType = 'room_boundaries';
		editingMode = 'draw';
		initialRoomBlindSpots = structuredClone(roomBlindSpots);
		initialRoomBoundaries = structuredClone(roomBoundaries);
	}

	// ZoneDrawPanel event handlers
	function handleDrawNameInput(e: CustomEvent) {
		drawModeName = e.detail;
		if (editingZone) {
			editingZone = { ...editingZone, name: drawModeName };
			updateLocalZone();
		}
	}

	function handleDrawColorSelect(e: CustomEvent) {
		drawModeColor = e.detail;
		if (editingZone) {
			editingZone = { ...editingZone, color: drawModeColor };
			updateLocalZone();
		}
	}

	// RoomDrawPanel event handlers
	function handleRoomModeChange(e: CustomEvent) {
		editingMode = e.detail;
	}

	// Room settings panel
	function handleMainZoneSettings() {
		showRoomSettings = true;
	}

	async function handleRoomSettingsSaved(e: CustomEvent) {
		const { zoneId, config: zoneConfig } = e.detail;
		roomSettingsSaving = true;
		try {
			const result = await updateZoneConfigRequest(device, zoneId, zoneConfig);
			if (result.isok) {
				roomSettingsPanelRef?.acknowledgeSaved?.();
				toast.open({
					type: 'success',
					title: $format('presence_toast_room_saved', {
						default: 'Room settings saved successfully.',
					}),
				});
			} else {
				toast.open({
					type: 'danger',
					title: $format('presence_toast_room_settings_failed', {
						default: 'Failed to save room settings.',
					}),
				});
			}
		} catch (error) {
			console.error('Error saving room settings:', error);
			toast.open({
				type: 'danger',
				title: $format('presence_toast_room_settings_failed', {
					default: 'Failed to save room settings.',
				}),
			});
		} finally {
			roomSettingsSaving = false;
		}
	}

	function handleTabClick(index: number) {
		switchTab(tabItems[index].key as ActiveTab);
	}

	function closeModal() {
		showModal = false;
		resetEditingState();
		selectFurniture(null);
	}

	// ── Per-zone person counts ────────────────────────────────────────────────
	function radarPointCellKey(rx: number, ry: number): string | null {
		const col = Math.floor(8 - 2 * rx);
		const row = Math.floor(2 * ry);
		if (row < 0 || row >= 16 || col < 0 || col >= 16) return null;
		return `${row},${col}`;
	}

	$: localZoneCells = localZones
		.filter((zone) => !hiddenZoneIds.has(zone.id))
		.map((zone) => ({
			zone,
			keys: zone.area?.length
				? zoneAreaToCellKeys(zone.area as [number, number, number, number][])
				: new Set<string>(),
		}));

	$: zonePersonCounts = (() => {
		const counts = new Map<number | string, number>();
		if (!radarPoints?.length) return counts;
		const cellKeys = radarPoints.map((pt) => radarPointCellKey(pt.x, pt.y));
		for (const { zone, keys } of localZoneCells) {
			const count = cellKeys.filter((k) => k !== null && keys.has(k!)).length;
			counts.set(zone.id, count);
		}
		return counts;
	})();
</script>

<div>
	<Row>
		<Col
			sm="12"
			md={editable ? 6 : 12}
			lg={editable ? 6 : 12}
			class="d-flex justify-content-center align-items-center radar-grid-col"
		>
			<div class="grid-container">
				<PresenceGrid
					bind:this={gridRef}
					viewOnly={!editable}
					zoneCells={localZoneCells}
					editable={(drawingMode || editingRoom) && !zoneSaving && !roomSaving}
					{showCoordinates}
					mode={editingMode}
					{radarPoints}
					{simulatedPoints}
					room={roomBoundaries}
					blind={roomBlindSpots}
					{editingRoom}
					{activeRoomEditType}
					{device}
					{mainZoneId}
					roomTintRgb={roomTintRgbForGrid}
					furnitureInteractive={activeTab === 'furnitures'}
					radarPosition={settings?.presence?.sensor?.position || 'center'}
					on:draw={handleDraw}
					on:roomDraw={handleRoomDraw}
				/>
			</div>
		</Col>

		{#if editable}
			<Col sm="12" md="6" lg="6" class="radar-sidebar-col">
				<div class="sidebar">
					<div class="sidebar__tabs">
						<Tabs useNewLayout={true}>
							{#each tabItems as item, i}
								<Tab
									active={i === activeTabIndex}
									hideRightSlot={true}
									useNewLayout={true}
									on:click={() => handleTabClick(i)}
								>
									{item.text}
								</Tab>
							{/each}
						</Tabs>
					</div>

					<div class="sidebar__content">
						<!-- ── Zones and boundaries tab ─────────────────────────── -->
						{#if activeTab === 'zones'}
							{#if editingZone}
								<ZoneDrawPanel
									title={editingZone.isNew
										? $format('presence_draw_create_zone', {
												default: 'Create zone',
											})
										: $format('presence_draw_edit_zone', { default: 'Edit zone' })}
									{drawModeName}
									{drawModeColor}
									enabled={zoneEnabledFromDevice(editingZone)}
									hasChanges={zoneHasChanges}
									mode={editingMode}
									saving={zoneSaving}
									on:modeChange={(e) => (editingMode = e.detail)}
									on:enabledChange={handleZoneEnabledChange}
									palette={ZONE_COLOR_PALETTE}
									on:nameInput={handleDrawNameInput}
									on:colorSelect={handleDrawColorSelect}
									on:save={saveZone}
									on:discard={cancelEditing}
									on:return={returnEditing}
								/>
							{:else if editingRoom}
								<RoomDrawPanel
									editType={activeRoomEditType}
									{editingMode}
									hasChanges={roomHasChanges}
									saving={roomSaving}
									on:modeChange={handleRoomModeChange}
									on:save={saveRoom}
									on:discard={cancelEditing}
									on:return={returnEditing}
								/>
							{:else if showRoomSettings && mainZoneId}
								<RoomSettingsPanel
									bind:this={roomSettingsPanelRef}
									zoneId={mainZoneId}
									zoneColor={mainZoneColor}
									{settings}
									{device}
									saving={roomSettingsSaving}
									on:save={handleRoomSettingsSaved}
									on:return={() => (showRoomSettings = false)}
								/>
							{:else}
								<ZonesBoundariesList
									zones={localZones}
									{mainZoneId}
									{zonePersonCounts}
									{hiddenZoneIds}
									zoneLimitReached={localZones.length >= ZONE_LIMIT}
									on:createZone={createNewZone}
									on:edit={handleZoneEdit}
									on:delete={deleteZone}
									on:toggleVisible={toggleZoneVisibility}
									on:editSettings={handleMainZoneSettings}
									on:editBlindSpots={handleBlindSpotsEdit}
									on:editBoundary={handleRoomBoundaryEdit}
								/>
							{/if}
						{/if}

						<!-- Furnitures and SensorConfig stay mounted (display:none when inactive)
					     so switching back is instant — no destroy/recreate overhead. -->
						<div
							class="tab-padded"
							class:tab-hidden={activeTab !== 'furnitures'}
							aria-hidden={activeTab !== 'furnitures'}
						>
							<Furnitures />
						</div>
						<div
							class="tab-padded"
							class:tab-hidden={activeTab !== 'config'}
							aria-hidden={activeTab !== 'config'}
						>
							<SensorConfigTab {device} {settings} {activeTab} />
						</div>
					</div>
				</div>
			</Col>
		{/if}
	</Row>
</div>

<style>
	.grid-container {
		position: relative;
		width: 100%;
		max-width: 306px;
	}

	.sidebar {
		display: flex;
		flex-direction: column;
		/* Fixed height = canvas max-width. This prevents the modal from
		   resizing or jumping when switching between tabs. */
		height: 420px;
		overflow: hidden;
		max-width: 100%;
	}

	.sidebar__tabs {
		padding: 0 8px;
		flex-shrink: 0;
	}

	/* Scrollable area below the tab bar — fills remaining height. */
	.sidebar__content {
		flex: 1;
		min-height: 0; /* required for flex children to scroll correctly */
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
	}

	/* Keeps Furnitures and SensorConfigTab mounted but invisible when inactive. */
	.tab-hidden {
		display: none;
	}

	.tab-padded {
		padding: 8px 12px 16px;
	}
</style>
